import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Pricing from "../components/Pricing";
import PricingFAQ from "../components/PricingFAQ";
import GoogleReviews from "../components/GoogleReviews";
import { usePageMeta, ORG_JSON_LD, productJsonLd } from "../lib/usePageMeta";
import {
  CheckCircle2,
  Sparkles,
  ChevronRight,
  BadgeCheck,
  Zap,
  Shield,
  Award,
  Users,
  Clock,
  BookOpen,
  Rocket,
  X,
  Minus,
} from "lucide-react";

const META = {
  seo: {
    title: "Pricing · CV Folks — AI CV Builder £9.99 · Professional CV Writing from £50",
    description:
      "Transparent UK CV writing prices. Online AI CV Builder from £9.99 — built by professional UK CV writers. Professional CVs from £50, complete packages with cover letter and LinkedIn from £95.",
    keywords:
      "cv writing prices uk, professional cv cost, ai cv builder price, online ai cv builder cost, cv writing service cost london",
  },
  jsonLd: [
    ORG_JSON_LD,
    productJsonLd({ id: "ai-cv-builder", name: "AI CV Builder", price: 9.99, description: "Online AI CV Builder developed by UK CV writers — instant ATS-friendly UK CV with matching cover letter, £9.99." }),
    productJsonLd({ id: "standard-cv", name: "Standard CV", price: 50, description: "Hand-written by a UK CV writer. ATS-friendly, tailored to your target role." }),
    productJsonLd({ id: "standard-package", name: "Standard Package (CV + Cover Letter + LinkedIn)", price: 95, description: "Standard CV + tailored Cover Letter + LinkedIn profile makeover." }),
    productJsonLd({ id: "premium-cv", name: "Premium CV", price: 125, description: "Senior-level CV with a 1-on-1 career consultation call." }),
    productJsonLd({ id: "premium-package", name: "Premium Package (CV + Cover Letter + LinkedIn + Call)", price: 155, description: "Our flagship — Premium CV, tailored Cover Letter, LinkedIn makeover and 45-min consultation." }),
  ],
};

// ---------- AI vs Professional comparison rows ----------
// icons: check | dash | text
const COMPARE = [
  { label: "Written by",              ai: "AI trained on UK CV craft",   pro: "Experienced UK CV writer" },
  { label: "Developed by CV experts", ai: true,                          pro: true },
  { label: "Professional writing methodology", ai: true,                 pro: true },
  { label: "ATS-friendly formatting", ai: true,                          pro: true },
  { label: "Matching cover letter",   ai: true,                          pro: "Included in Packages" },
  { label: "LinkedIn profile makeover", ai: false,                       pro: "Included in Packages" },
  { label: "1-on-1 career consultation call", ai: false,                 pro: "Premium tiers" },
  { label: "Turnaround",              ai: "Instant (< 5 min)",           pro: "24–48 hours" },
  { label: "Unlimited edits",         ai: "6 months",                    pro: "6–12 months" },
  { label: "60-day interview guarantee", ai: false,                      pro: true },
  { label: "£9.99 credit toward upgrade", ai: true,                      pro: "—" },
];

function Cell({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
        <CheckCircle2 className="h-4 w-4" /> Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/40">
        <X className="h-4 w-4" /> No
      </span>
    );
  }
  if (value === "—") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/40">
        <Minus className="h-4 w-4" />
      </span>
    );
  }
  return <span className="text-sm text-foreground/80">{value}</span>;
}

const AI_FEATURES = [
  { icon: BookOpen, label: "Developed by professional UK CV writers" },
  { icon: Shield, label: "Recruiter-informed ATS methodology" },
  { icon: Award, label: "Premium templates — Classic, Modern, Compact" },
  { icon: Sparkles, label: "AI-written cover letter included" },
  { icon: Zap, label: "Ready in under 5 minutes" },
  { icon: Users, label: "£9.99 credits toward any Professional package" },
];

export default function PricingPage() {
  usePageMeta(META);
  return (
    <>
      <Navbar />
      <main data-testid="pricing-page" className="pt-24 md:pt-32 bg-background overflow-hidden">
        {/* ================= HERO ================= */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 mb-14 md:mb-20">
          <p className="label-eyebrow">Pricing</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] max-w-3xl">
            Two ways to a stronger CV —{" "}
            <span className="text-brand">pick what suits you.</span>
          </h1>
          <p className="text-base md:text-lg text-foreground/70 mt-5 max-w-2xl">
            Start with our £9.99 Online AI CV Builder — developed by
            professional UK CV writers — or hire an experienced UK writer from
            £50. Your £9.99 credits toward any Professional package if you
            upgrade later.
          </p>
        </section>

        {/* ================= LARGE AI CV BUILDER CTA ================= */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div
            data-testid="pricing-ai-large-cta"
            className="relative overflow-hidden rounded-[2.5rem] border border-brand/30 bg-gradient-to-br from-brand/8 via-background to-brand/12 p-8 md:p-14 shadow-lg"
          >
            {/* decorative blobs */}
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand/25 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand/15 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand/12 border border-brand/25 px-3 py-1.5 text-[11px] font-bold tracking-widest uppercase text-brand">
                  <Rocket className="h-3.5 w-3.5" /> Instant · £9.99 · No subscription
                </div>
                <h2 className="font-serif text-3xl md:text-5xl font-bold mt-4 leading-[1.05]">
                  Build a professional CV online with{" "}
                  <span className="text-brand">AI — for just £9.99.</span>
                </h2>
                <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
                  Our Online AI CV Builder isn&apos;t a generic tool. It&apos;s
                  developed by professional UK CV writers using curated CV
                  writing methodology, recruiter-informed best practices and
                  ATS optimisation principles.
                </p>

                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {AI_FEATURES.map((f) => (
                    <li
                      key={f.label}
                      className="flex items-start gap-3 rounded-xl bg-background/70 border border-border px-3.5 py-3 backdrop-blur-sm"
                    >
                      <span className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
                        <f.icon className="h-4 w-4" />
                      </span>
                      <span className="text-sm font-semibold leading-snug text-foreground/90">
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to="/ai-cv-builder"
                    data-testid="pricing-ai-cta-primary"
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold shadow-lg shadow-brand/20 hover:shadow-brand/40 hover:-translate-y-0.5 transition"
                  >
                    <Sparkles className="h-5 w-5" /> Build my CV — £9.99
                  </Link>
                  <Link
                    to="/ai-cv-maker-builder"
                    data-testid="pricing-ai-cta-secondary"
                    className="inline-flex items-center gap-2 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 px-6 py-4 text-sm font-semibold transition"
                  >
                    See how it works <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <p className="text-xs text-foreground/60 mt-4">
                  Secure PayPal payment · UK company · No hidden fees ·
                  Preview before you pay
                </p>
              </div>

              {/* Price card */}
              <div className="lg:col-span-5">
                <div className="relative rounded-3xl border border-border bg-card p-7 md:p-8 shadow-2xl hover:-translate-y-1 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]">
                      <BadgeCheck className="h-4 w-4" /> Online AI CV Builder
                    </div>
                    <div className="rounded-full bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                      Best value
                    </div>
                  </div>

                  <p className="mt-4 font-serif text-6xl md:text-7xl font-bold leading-none">
                    £9.99
                  </p>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    One-off · no subscription
                  </p>

                  <div className="mt-7 pt-7 border-t border-border space-y-3.5">
                    {[
                      "CV Maker access (Classic / Modern / Compact)",
                      "AI-written cover letter",
                      "PDF + DOCX download",
                      "6 months of edits",
                      "Sarah AI Consultant included",
                      "Preview before you pay",
                    ].map((row) => (
                      <div
                        key={row}
                        className="flex items-center justify-between gap-4 text-sm"
                      >
                        <span className="text-foreground/80">{row}</span>
                        <CheckCircle2 className="h-4 w-4 text-brand shrink-0" />
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/ai-cv-builder"
                    data-testid="pricing-ai-card-cta"
                    className="btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold shadow-lg shadow-brand/20 hover:shadow-brand/40 transition"
                  >
                    Get started <ChevronRight className="h-4 w-4" />
                  </Link>
                  <p className="mt-3 text-center text-[11px] text-foreground/60">
                    £9.99 credits toward any Professional package
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= OR SEPARATOR ================= */}
        <div
          data-testid="pricing-or-separator"
          className="max-w-6xl mx-auto px-6 md:px-10 pb-10 md:pb-14"
        >
          <div className="flex items-center gap-6">
            <div className="flex-1 h-px bg-border" />
            <span className="inline-flex items-center justify-center h-14 w-14 rounded-full border-2 border-brand bg-background font-serif text-lg font-bold text-brand shadow-sm">
              OR
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>
        </div>

        {/* ================= PROFESSIONAL PACKAGES ================= */}
        <div className="max-w-6xl mx-auto px-6 md:px-10 mb-8 text-center">
          <p className="label-eyebrow">Professional CV Writing</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.02]">
            Want an experienced{" "}
            <span className="text-brand">UK CV writer</span> instead?
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground/70 max-w-2xl mx-auto">
            Hand-crafted CVs from £50 by UK-based writers who know your sector,
            backed by our 60-day interview guarantee.
          </p>
        </div>
        <Pricing />

        {/* ================= COMPARISON (redesigned) ================= */}
        <section
          data-testid="pricing-compare"
          className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28"
        >
          <div className="text-center max-w-2xl mx-auto">
            <p className="label-eyebrow">Side-by-side comparison</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
              AI Builder or Professional Writer —{" "}
              <span className="text-brand">what&apos;s right for you?</span>
            </h2>
            <p className="mt-5 text-foreground/70 leading-relaxed">
              Both routes are built on the same professional CV writing
              methodology developed by our UK CV writers. Pick the pace and
              level of support that suits your situation.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-6">
            {/* --- AI card (premium styling to differentiate) --- */}
            <div
              data-testid="pricing-compare-ai-card"
              className="group relative rounded-3xl border-2 border-brand/40 bg-gradient-to-br from-brand/5 via-background to-brand/10 p-8 md:p-10 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition overflow-hidden"
            >
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-brand/20 blur-3xl" />
              <div className="absolute top-5 right-5 rounded-full bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 shadow">
                Fastest · £9.99
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand/12 border border-brand/25 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-brand">
                  <Sparkles className="h-3 w-3" /> Online AI CV Builder
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold mt-4 leading-tight">
                  AI, built by professional UK CV writers.
                </h3>
                <p className="mt-3 text-foreground/70 leading-relaxed text-sm md:text-base">
                  Instant CV + cover letter, guided by the same recruiter-informed
                  methodology, ATS best practices and premium templates our
                  writers use.
                </p>

                <p className="mt-6 font-serif text-5xl font-bold">£9.99</p>
                <p className="text-xs text-muted-foreground mt-1">
                  One-off · no subscription
                </p>

                <ul className="mt-6 space-y-2.5">
                  {[
                    "Developed by professional UK CV writers",
                    "Recruiter-informed CV structure & ATS methodology",
                    "AI-written cover letter included",
                    "PDF + DOCX · Classic / Modern / Compact",
                    "Unlimited edits for 6 months",
                    "£9.99 credits toward any Professional package",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                      <span className="text-foreground/85">{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/ai-cv-builder"
                  data-testid="pricing-compare-ai-cta"
                  className="btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
                >
                  <Sparkles className="h-4 w-4" /> Start for £9.99
                </Link>
              </div>
            </div>

            {/* --- Professional card --- */}
            <div
              data-testid="pricing-compare-pro-card"
              className="group relative rounded-3xl border border-border bg-card p-8 md:p-10 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition overflow-hidden"
            >
              <div className="absolute top-5 right-5 rounded-full border border-border bg-background text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 text-foreground/70">
                From £50
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest">
                <Users className="h-3 w-3" /> Professional CV Writing
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mt-4 leading-tight">
                Hand-written by a UK CV writer.
              </h3>
              <p className="mt-3 text-foreground/70 leading-relaxed text-sm md:text-base">
                A dedicated UK-based writer who knows your sector will craft
                your CV from a full career discovery — plus a 60-day interview
                guarantee.
              </p>

              <p className="mt-6 font-serif text-5xl font-bold">
                £50<span className="text-xl text-foreground/60"> — £155</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Standard, Standard Package, Premium & Premium Package
              </p>

              <ul className="mt-6 space-y-2.5">
                {[
                  "Hand-written by an experienced UK CV writer",
                  "Personalised for your target role and industry",
                  "24–48 hour first draft turnaround",
                  "Unlimited edits (6–12 months)",
                  "Cover letter / LinkedIn / Consultation (Packages)",
                  "60-day interview guarantee",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-foreground mt-0.5 shrink-0" />
                    <span className="text-foreground/85">{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/services/cv-writing"
                data-testid="pricing-compare-pro-cta"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 px-6 py-3.5 text-sm font-semibold transition"
              >
                Hire a UK writer — from £50 <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Detailed compare table */}
          <div className="mt-14 rounded-3xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-3 bg-muted/40 text-left">
              <div className="px-6 py-4 font-semibold text-sm">Feature</div>
              <div className="px-6 py-4 font-semibold text-sm text-center">
                <span className="inline-flex items-center gap-1.5 text-brand">
                  <Sparkles className="h-4 w-4" /> AI Builder · £9.99
                </span>
              </div>
              <div className="px-6 py-4 font-semibold text-sm text-center">
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> Professional · from £50
                </span>
              </div>
            </div>
            <div className="divide-y divide-border">
              {COMPARE.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-3 items-center hover:bg-muted/20 transition"
                >
                  <div className="px-6 py-3.5 text-sm text-foreground/85">
                    {row.label}
                  </div>
                  <div className="px-6 py-3.5 text-center">
                    <Cell value={row.ai} />
                  </div>
                  <div className="px-6 py-3.5 text-center">
                    <Cell value={row.pro} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to="/ai-cv-builder"
              data-testid="pricing-cta-ai"
              className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold shadow-lg shadow-brand/20 hover:shadow-brand/40 hover:-translate-y-0.5 transition"
            >
              <Sparkles className="h-4 w-4" /> Try AI CV Builder — £9.99
            </Link>
            <Link
              to="/services/cv-writing"
              data-testid="pricing-cta-pro"
              className="inline-flex items-center gap-2 rounded-full border border-foreground px-7 py-3.5 text-sm font-semibold hover:bg-foreground hover:text-background transition"
            >
              <Users className="h-4 w-4" /> Hire a UK writer — from £50
            </Link>
          </div>
        </section>

        <GoogleReviews />
        <PricingFAQ />
      </main>
      <Footer />
    </>
  );
}
